import { useCallback, useEffect, useRef, useState } from "react";
import {
  getStoredDemoBudgets,
  getStoredDemoExpenses,
  upsertStoredDemoBudget,
} from "../utils/demoStorage";
import { isDemoMode as getIsDemoMode } from "../utils/demoAuth";
import { BASE_URL } from "../utils/apiConfig";
import { buildMonthYearQuery, getMonthName } from "../utils/monthYear";

const defaultBudgets = [
  "rent",
  "food",
  "transport",
  "grocery",
  "shopping",
  "utilities",
  "entertainment",
  "medical",
  "education",
  "beverage",
  "others",
];

function Budget({ selectedMonth, selectedYear }) {
  const budgetAmountInputRef = useRef(null);
  const budgetFormRef = useRef(null);
  const [demoMode, setDemoMode] = useState(
    () => getIsDemoMode()
  );
  const [expenses, setExpenses] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(defaultBudgets[0]);
  const [customBudgetCategory, setCustomBudgetCategory] = useState("");
  const [budgetAmount, setBudgetAmount] = useState("");
  const [savingBudget, setSavingBudget] = useState(false);
  const [demoMessage, setDemoMessage] = useState("");
  const [editingCategory, setEditingCategory] = useState("");
  const [draftBudgetValues, setDraftBudgetValues] = useState({});

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("token");

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const loadExpenses = useCallback(async () => {
    if (demoMode) {
      setExpenses(getStoredDemoExpenses(selectedMonth, selectedYear));
      return;
    }

    try {
      const query = buildMonthYearQuery(selectedMonth, selectedYear);
      const res = await fetch(`${BASE_URL}/api/expenses?${query}`, {
        headers: getAuthHeaders(),
      });

      const data = await res.json();
      setExpenses(Array.isArray(data) ? data : []);
    } catch {
      setExpenses([]);
    }
  }, [demoMode, getAuthHeaders, selectedMonth, selectedYear]);

  const loadBudgets = useCallback(async () => {
    if (demoMode) {
      setBudgets(getStoredDemoBudgets(selectedMonth, selectedYear));
      return;
    }

    try {
      const query = buildMonthYearQuery(selectedMonth, selectedYear);
      const res = await fetch(`${BASE_URL}/api/budgets?${query}`, {
        headers: getAuthHeaders(),
      });

      const data = await res.json();
      setBudgets(Array.isArray(data) ? data : []);
    } catch {
      setBudgets([]);
    }
  }, [demoMode, getAuthHeaders, selectedMonth, selectedYear]);

  useEffect(() => {
    setDemoMode(getIsDemoMode());
  }, []);

  useEffect(() => {
    loadExpenses();
    loadBudgets();
  }, [loadExpenses, loadBudgets]);

  const formatMoney = (value) => Number(value || 0).toLocaleString("en-IN");
  const formatCategory = (category) =>
    (category || "others")
      .replace(/-/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

  const showDemoDisabledMessage = () => {
    setDemoMessage("Demo budget changes are saved locally only.");
  };

  const categoryTotals = expenses.reduce((acc, expense) => {
    const category = (expense.category || "others").toLowerCase();
    acc[category] = (acc[category] || 0) + Number(expense.amount || 0);
    return acc;
  }, {});

  const getBudgetAmount = (category) => {
    const found = budgets.find((b) => b.category === category);
    return found ? Number(found.amount || 0) : 0;
  };

  const updateBudget = async (category, value) => {
    if (demoMode) {
      upsertStoredDemoBudget(category, value, selectedMonth, selectedYear);
      await loadBudgets();
      return;
    }

    try {
      const amount = Number(value);

      await fetch(`${BASE_URL}/api/budgets`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          category,
          amount,
          month: selectedMonth,
          year: selectedYear,
        }),
      });

      await loadBudgets();
    } catch {
      setDemoMessage("Unable to update budget right now.");
    }
  };

  const handleAddBudget = async (event) => {
    event.preventDefault();

    if (demoMode) {
      const targetCategory =
        customBudgetCategory.trim().toLowerCase() || selectedCategory;

      if (!targetCategory || budgetAmount === "") return;

      setSavingBudget(true);

      try {
        await updateBudget(targetCategory, budgetAmount);
        setCustomBudgetCategory("");
        setBudgetAmount("");
        showDemoDisabledMessage();
      } finally {
        setSavingBudget(false);
      }

      return;
    }

    const targetCategory =
      customBudgetCategory.trim().toLowerCase() || selectedCategory;

    if (!targetCategory || budgetAmount === "") return;

    setSavingBudget(true);

    try {
      await updateBudget(targetCategory, budgetAmount);
      setCustomBudgetCategory("");
      setBudgetAmount("");
    } finally {
      setSavingBudget(false);
    }
  };

  const focusAddBudgetForm = () => {
    if (demoMode) {
      budgetFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      budgetAmountInputRef.current?.focus();
      return;
    }

    budgetFormRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    budgetAmountInputRef.current?.focus();
  };

  const startEditingBudget = (category, currentAmount) => {
    if (demoMode) {
      setEditingCategory(category);
      setDraftBudgetValues((values) => ({
        ...values,
        [category]: currentAmount,
      }));
      return;
    }

    setEditingCategory(category);
    setDraftBudgetValues((values) => ({
      ...values,
      [category]: currentAmount,
    }));
  };

  const cancelEditingBudget = () => {
    setEditingCategory("");
  };

  const saveEditedBudget = async (category) => {
    if (demoMode) {
      const nextValue = draftBudgetValues[category];

      if (nextValue === undefined || nextValue === "") return;

      await updateBudget(category, nextValue);
      setEditingCategory("");
      showDemoDisabledMessage();
      return;
    }

    const nextValue = draftBudgetValues[category];

    if (nextValue === undefined || nextValue === "") return;

    await updateBudget(category, nextValue);
    setEditingCategory("");
  };

  const categoryNames = [
    ...new Set([
      ...defaultBudgets,
      ...budgets.map((budget) => budget.category || "others"),
      ...expenses.map((expense) => expense.category || "others"),
    ]),
  ].map((category) => String(category).toLowerCase());

  const budgetCards = categoryNames.map((category) => {
    const spent = categoryTotals[category] || 0;
    const budget = getBudgetAmount(category);
    const rawPercentage =
      budget > 0 ? (spent / budget) * 100 : spent > 0 ? 101 : 0;
    const percentage = Math.min(rawPercentage, 100);
    const remaining = budget - spent;
    const status =
      rawPercentage > 100
        ? "over"
        : rawPercentage >= 70
        ? "warning"
        : "safe";
    const statusLabel =
      status === "over"
        ? "Over Budget"
        : status === "warning"
        ? "Warning"
        : "Safe";

    return {
      category,
      spent,
      budget,
      rawPercentage,
      percentage,
      remaining,
      status,
      statusLabel,
    };
  });

  const totalBudget = budgetCards.reduce(
    (sum, item) => sum + Number(item.budget || 0),
    0
  );
  const totalSpent = budgetCards.reduce(
    (sum, item) => sum + Number(item.spent || 0),
    0
  );
  const remainingBudget = totalBudget - totalSpent;
  const overBudgetCount = budgetCards.filter((item) => item.status === "over")
    .length;
  const warningCount = budgetCards.filter((item) => item.status === "warning")
    .length;
  const trackedCategoryCount = Math.max(
    budgetCards.filter((item) => item.budget > 0 || item.spent > 0).length,
    1
  );
  const remainingPercentage =
    totalBudget > 0 ? (remainingBudget / totalBudget) * 100 : 100;
  const remainingScore = Math.max(
    0,
    Math.min(30, Math.round(Math.max(remainingPercentage, 0) * 0.75))
  );
  const overBudgetPenalty = Math.min(
    34,
    Math.round(overBudgetCount * 8 + (overBudgetCount / trackedCategoryCount) * 18)
  );
  const warningPenalty = Math.min(
    18,
    Math.round(warningCount * 4 + (warningCount / trackedCategoryCount) * 10)
  );
  const budgetHealthScore = Math.max(
    0,
    Math.min(100, Math.round(70 + remainingScore - overBudgetPenalty - warningPenalty))
  );
  const budgetHealthStatus =
    budgetHealthScore >= 75
      ? "Healthy Risk"
      : budgetHealthScore >= 50
      ? "Moderate Risk"
      : "Critical Risk";

  return (
    <div className="page budget-premium-page">
      <div className="budget-topbar">
        <div>
          <div className="dashboard-title-badges">
            <span className="soft-badge">Budget Planner</span>
            {demoMode && <span className="demo-mode-badge">Demo Mode</span>}
          </div>
          <h2>Budget Control</h2>
          <p>Set category limits, monitor spending, and avoid overspending.</p>
        </div>
      </div>

      {demoMode && (
        <div className="demo-mode-notice">
          Demo budget changes are saved locally and never sent to your personal APIs.
        </div>
      )}

      {demoMessage && <div className="demo-mode-notice">{demoMessage}</div>}

      <div className="budget-summary-grid">
        <div className="report-summary-card income">
          <span>Total Budget</span>
          <strong>{"\u20b9"}{formatMoney(totalBudget)}</strong>
          <p>{getMonthName(selectedMonth)} {selectedYear}</p>
        </div>

        <div className="report-summary-card expense">
          <span>Total Spent</span>
          <strong>{"\u20b9"}{formatMoney(totalSpent)}</strong>
          <p>{expenses.length} expense entries</p>
        </div>

        <div className="report-summary-card savings">
          <span>Remaining Budget</span>
          <strong className={remainingBudget >= 0 ? "positive" : "negative"}>
            {"\u20b9"}{formatMoney(remainingBudget)}
          </strong>
          <p>{remainingBudget >= 0 ? "Available to spend" : "Overspent this month"}</p>
        </div>

        <div className="report-summary-card balance">
          <span>Budget Health</span>
          <strong className={budgetHealthScore >= 50 ? "positive" : "negative"}>
            {budgetHealthScore} / 100
          </strong>
          <p>{budgetHealthStatus}</p>
        </div>
      </div>

      <div className="budget-workspace-grid">
        <div className="budget-cards-grid">
          {budgetCards.map((item) => (
            <div
              key={item.category}
              className={`premium-panel budget-premium-card ${item.status}`}
            >
              <div className="budget-card-header">
                <div>
                  <span className="soft-badge">{formatCategory(item.category)}</span>
                  <h3>{formatCategory(item.category)}</h3>
                </div>

                <span className={`budget-status-badge ${item.status}`}>
                  {item.statusLabel}
                </span>
              </div>

              <div className="budget-metric-grid">
                <div>
                  <span>Budget Amount</span>
                  <strong>{"\u20b9"}{formatMoney(item.budget)}</strong>
                </div>
                <div>
                  <span>Spent Amount</span>
                  <strong>{"\u20b9"}{formatMoney(item.spent)}</strong>
                </div>
                <div>
                  <span>Remaining Amount</span>
                  <strong className={item.remaining >= 0 ? "positive" : "negative"}>
                    {"\u20b9"}{formatMoney(item.remaining)}
                  </strong>
                </div>
              </div>

              <div className="budget-progress-meta">
                <span>Usage</span>
                <strong>{Math.round(item.rawPercentage)}%</strong>
              </div>

              <div className="budget-progress">
                <div style={{ width: `${item.percentage}%` }}></div>
              </div>

              <div className={`budget-inline-edit ${editingCategory === item.category ? "active" : ""}`}>
                <div className="budget-edit-label-row">
                  <span>Budget Limit</span>
                  <button
                    type="button"
                    className="budget-icon-button"
                    aria-label={`Edit ${formatCategory(item.category)} budget`}
                    onClick={() => startEditingBudget(item.category, item.budget)}
                  >
                    &#9998;
                  </button>
                </div>

                {editingCategory === item.category && (
                  <div className="budget-edit-control">
                    <input
                      type="number"
                      value={draftBudgetValues[item.category] ?? item.budget}
                      onChange={(e) =>
                        setDraftBudgetValues((values) => ({
                          ...values,
                          [item.category]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          saveEditedBudget(item.category);
                        }

                        if (e.key === "Escape") {
                          cancelEditingBudget();
                        }
                      }}
                      placeholder="Set budget"
                    />

                    <button
                      type="button"
                      className="budget-icon-button save"
                      aria-label={`Save ${formatCategory(item.category)} budget`}
                      onClick={() => saveEditedBudget(item.category)}
                    >
                      &#10003;
                    </button>

                    <button
                      type="button"
                      className="budget-icon-button subtle"
                      aria-label="Cancel budget edit"
                      onClick={cancelEditingBudget}
                    >
                      &#215;
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <form
          ref={budgetFormRef}
          className="premium-panel budget-form-card"
          onSubmit={handleAddBudget}
        >
          <div className="panel-title-row">
            <div>
              <button
                type="button"
                className="soft-badge budget-add-chip"
                onClick={focusAddBudgetForm}
              >
                + Add Budget
              </button>
              <h3>
                <span className="budget-form-title-icon" aria-hidden="true">+</span>
                Category Limit
              </h3>
            </div>
          </div>

          <label>
            <span>Existing Category</span>
            <select
              className="budget-category-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categoryNames.map((category) => (
                <option key={category} value={category}>
                  {formatCategory(category)}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>New Category</span>
            <input
              type="text"
              value={customBudgetCategory}
              onChange={(e) => setCustomBudgetCategory(e.target.value)}
              placeholder="Optional custom category"
            />
          </label>

          <label>
            <span>Budget Amount</span>
            <input
              ref={budgetAmountInputRef}
              type="number"
              value={budgetAmount}
              onChange={(e) => setBudgetAmount(e.target.value)}
              placeholder="Enter amount"
            />
          </label>

          <button type="submit" className="glow-button" disabled={savingBudget}>
            {savingBudget ? "Saving..." : "Save Budget"}
          </button>

          {demoMode && (
            <p className="budget-form-note">Demo edits stay in local sample data.</p>
          )}
        </form>
      </div>
    </div>
  );
}

export default Budget;
